import {
  collection,
  query,
  where,
  getDocs,
  getDoc,
  doc,
  documentId,
  updateDoc,
  serverTimestamp,
  onSnapshot
} from 'firebase/firestore';
import { db } from '../firebase';
import { listingListedAtMs } from './sortPedalsByListedAt';
import { withTransientRetry } from './transientReadRetry';
import { createCoalescingRunner } from './coalescingRunner';
import { offerModeOf } from './offerMode';

/**
 * Dashboard "X new" badge counters for the Wantlist Matches and Match
 * Opportunities carousel headers.
 *
 * Both counters derive from the server-maintained `wantsMyPedals` collection,
 * which `functions/matchMaker.js` writes one doc per (want, matching listing)
 * pair when:
 *   - a new listing is created that matches an existing want (findMatchesOnListingCreate)
 *   - a listing is updated/reactivated and now matches an existing want (findMatchesOnListingUpdate)
 *   - a new want is created that matches an existing listing (findMatchesOnWantCreate)
 *   - an existing want is broadened and now matches existing listings (findMatchesOnWantChange)
 *
 * Each `wantsMyPedals` doc carries `wantingUserId`, `pedalOwnerId`, `pedalId`,
 * and `wantId`. matchMaker writes these docs with a blind `.set()`, so the
 * doc's own `createdAt` is NOT a reliable "first matched at" timestamp — it is
 * overwritten whenever the (want, listing) pair is re-evaluated (e.g. on a
 * listing edit). These counters therefore never use `wmp.createdAt` for a
 * correctness decision. Instead they derive "new since last visit" purely from
 * the stable source-of-truth timestamps that are never rewritten:
 *   - `wantList/{wantId}.createdAt`  — when the want was created
 *   - `listings/{pedalId}` listed-at — `reactivatedAt` if present, else
 *     `createdAt` (via listingListedAtMs), i.e. when the listing last went live
 *
 * `wmp.createdAt` is still read as a cheap BOUNDING HINT only (to pick which
 * candidates to fetch before the read caps); since a re-write can only push it
 * forward in time, it never hides a genuinely-recent candidate.
 *
 * Per-user "last viewed" cursors live on `users/{uid}` as plain Timestamp
 * fields (`lastViewedWantListMatchesAt`, `lastViewedMatchOpportunitiesAt`).
 * Neither is in the `users/{uid}` rules deny-list (firestore.rules:80-92),
 * so the owner can self-write them with no rules change.
 */

const DISPLAY_CAP = 9; // Render as "9+" past this; no behavioral effect
const QUERY_LIMIT = 50; // Bound reads; if a user blows past 50 unseen we still render "9+"
// Cap the Match Opportunities fan-out (one listings query per interested user)
// so a very popular pedal owner doesn't trigger hundreds of reads per dashboard
// load. Interested users are scanned most-recently-interested first.
const MAX_INTERESTED_USERS_SCANNED = 50;

/**
 * Coerce a Firestore Timestamp / Date / numeric ms to milliseconds since epoch.
 * Mirrors the asTimestamp pattern in functions/processBiWeeklyMatchEmailTask.js
 * but on the client-Timestamp shape (toMillis is the canonical accessor).
 * Returns 0 for unrecognized shapes — callers treat 0 as "no cursor / very old".
 */
function tsToMs(v) {
  if (!v) return 0;
  if (typeof v.toMillis === 'function') return v.toMillis();
  if (typeof v.toDate === 'function') return v.toDate().getTime();
  if (v instanceof Date) return v.getTime();
  if (typeof v === 'number') return v;
  return 0;
}

/**
 * Read both viewed cursors in a single users/{uid} fetch. Returns 0 for any
 * cursor that's missing — callers handle that as "first visit, mark all seen".
 *
 * Returning 0 (not null) keeps the comparison logic in the count helpers
 * uniform: every wmp.createdAt > 0 will be true, so we'd "show all" — but
 * the dashboard never reaches that branch because it calls
 * markFirstVisitIfNeeded before counting.
 */
async function readViewedCursors(userId) {
  try {
    // This read failed with "client is offline" in the captured 2026-09-29
    // incident (badges then silently rendered as if nothing was new). Retry
    // once on a transient blip; both-fail keeps the existing 0-cursor
    // fallback below. Web-only via the helper's platform gate.
    const userSnap = await withTransientRetry('cursors-read', () => getDoc(doc(db, 'users', userId)));
    if (!userSnap.exists()) {
      return { wlmMs: 0, moMs: 0 };
    }
    const data = userSnap.data();
    return {
      wlmMs: tsToMs(data.lastViewedWantListMatchesAt),
      moMs: tsToMs(data.lastViewedMatchOpportunitiesAt)
    };
  } catch (err) {
    console.error('❌ Error reading viewed cursors:', err);
    return { wlmMs: 0, moMs: 0 };
  }
}

/**
 * On first visit (no cursor on the user doc), claim the cursor immediately so
 * the badge starts at 0 instead of "everything you've ever matched is new."
 * Callers should invoke this BEFORE counting on dashboard mount.
 *
 * Idempotent: only writes the field if it's currently missing. Safe to call
 * on every dashboard mount.
 *
 * @returns {Promise<{wlmInitialized: boolean, moInitialized: boolean}>}
 *   Whether either cursor was just claimed (caller can short-circuit the
 *   count fetch when both were just claimed — count is necessarily 0).
 */
export async function markFirstVisitIfNeeded(userId) {
  if (!userId) return { wlmInitialized: false, moInitialized: false };
  try {
    const userRef = doc(db, 'users', userId);
    // Only the READ is retried (helper contract: reads only). The updateDoc
    // below is a write and deliberately keeps its single attempt.
    const userSnap = await withTransientRetry('firstvisit-read', () => getDoc(userRef));
    if (!userSnap.exists()) {
      return { wlmInitialized: false, moInitialized: false };
    }
    const data = userSnap.data();
    const updates = {};
    let wlmInitialized = false;
    let moInitialized = false;
    if (data.lastViewedWantListMatchesAt == null) {
      updates.lastViewedWantListMatchesAt = serverTimestamp();
      wlmInitialized = true;
    }
    if (data.lastViewedMatchOpportunitiesAt == null) {
      updates.lastViewedMatchOpportunitiesAt = serverTimestamp();
      moInitialized = true;
    }
    if (wlmInitialized || moInitialized) {
      await updateDoc(userRef, updates);
      console.log('🆕 First visit: claimed match counter cursor(s)', {
        wlmInitialized,
        moInitialized
      });
    }
    return { wlmInitialized, moInitialized };
  } catch (err) {
    console.error('❌ Error marking first-visit cursors:', err);
    return { wlmInitialized: false, moInitialized: false };
  }
}

// Firestore's ceiling for `in` / documentId() disjunctions. Every query below
// carries exactly ONE `in` filter (plus at most one equality), so the whole
// disjunction budget is available to the chunk.
const IN_CHUNK = 30;

function chunk(ids, size = IN_CHUNK) {
  const out = [];
  for (let i = 0; i < ids.length; i += size) out.push(ids.slice(i, i + size));
  return out;
}

/**
 * Fetch documents of `col` by id, in documentId() `in` batches of 30 instead of
 * one getDoc per id (Stage 2, 2026-10-31: the wantlist counter issued up to
 * QUERY_LIMIT individual reads per pass). Contract preserved from the
 * per-id version: ids are deduplicated, a missing document maps to null, and
 * a failed batch maps every id in that batch to null (the per-id version
 * mapped a failed read to null).
 * @returns {Promise<Map<string, object|null>>}
 */
async function fetchDocsByIds(col, ids) {
  const map = new Map();
  const unique = [...new Set(ids.filter(Boolean))];
  await Promise.all(
    chunk(unique).map(async (slice) => {
      try {
        const snap = await getDocs(query(collection(db, col), where(documentId(), 'in', slice)));
        snap.forEach((d) => map.set(d.id, d.data()));
      } catch (err) {
        console.error(`Error fetching ${col} batch for counter:`, err);
      }
      for (const id of slice) if (!map.has(id)) map.set(id, null);
    })
  );
  return map;
}

/** Listings by pedalId: ghost filter + listed-at tests. Map<pedalId, listing|null>. */
const fetchListingsByIds = (pedalIds) => fetchDocsByIds('listings', pedalIds);

/**
 * wantList docs by wantId. Both counters need the want's stable `createdAt`:
 * Wantlist Matches compares it to the listing's listed-at to decide which side
 * triggered the match; Match Opportunities takes the min across an interested
 * user's wants as that user's relationship-start. Map<wantId, want|null>.
 */
const fetchWantsByIds = (wantIds) => fetchDocsByIds('wantList', wantIds);

/**
 * Active listings of several owners in `ownerId in` batches of 30, instead of
 * one query per owner (Stage 2: up to MAX_INTERESTED_USERS_SCANNED queries per
 * pass). No per-owner limit existed before and none is introduced: each owner
 * gets ALL their active listings, exactly as the per-owner query returned.
 * A failed batch yields [] for every owner in it (the per-owner version
 * yielded [] for a failed owner).
 * @returns {Promise<Map<string, Array<{id: string, data: object}>>>}
 */
async function fetchActiveListingsByOwners(ownerIds) {
  const map = new Map(ownerIds.map((uid) => [uid, []]));
  await Promise.all(
    chunk([...new Set(ownerIds)]).map(async (slice) => {
      try {
        const snap = await getDocs(
          query(collection(db, 'listings'), where('ownerId', 'in', slice), where('active', '==', true))
        );
        snap.forEach((d) => {
          const data = d.data();
          if (map.has(data.ownerId)) map.get(data.ownerId).push({ id: d.id, data });
        });
      } catch (err) {
        console.error("Error fetching interested users' listings for counter:", err);
      }
    })
  );
  return map;
}

/**
 * Listing-level exclusions shared by both counters: the listing must be
 * available to actually surface on either destination page. Mirrors the
 * filter applied by src/utils/wantListMatches.js and
 * src/utils/recommendations.js so badge counts reconcile with the page.
 */
function isListingUnavailable(listing) {
  if (!listing) return true; // listing was deleted entirely
  if (listing.active === false) return true;
  if (listing.inTrade === true) return true;
  if (listing.hiddenFromPedalboard === true) return true;
  if (listing.needsReactivation === true) return true;
  return false;
}

/**
 * Wantlist Matches exclusion: skip the listing if it's unavailable, OR
 * if it's owned by the current user (a wanter shouldn't count their own
 * pedals — wantsMyPedals docs with self-owned pedals shouldn't exist
 * because matchmaker filters self in the opposite-side query, but this
 * is defense-in-depth against a future regression).
 */
function isExcludedForWantListMatches(listing, currentUserId) {
  if (isListingUnavailable(listing)) return true;
  if (listing.ownerId === currentUserId) return true;
  return false;
}

/**
 * Match Opportunities card availability: mirror exactly what the carousel
 * (src/utils/recommendations.js → getPedalsFromUsersWhoWantMine) renders so the
 * badge can never disagree with the visible cards. The carousel queries an
 * interested user's `active === true` listings and then drops `inTrade` /
 * `hiddenFromPedalboard`. Returns true if the pedal would NOT appear as a card.
 */
function isHiddenFromMatchOpportunities(pedal) {
  if (!pedal) return true;
  if (pedal.active === false) return true;
  if (pedal.inTrade === true) return true;
  if (pedal.hiddenFromPedalboard === true) return true;
  if (offerModeOf(pedal) === 'sell') return true; // Trade Opportunities are trades (plan §5)
  return false;
}

/**
 * Ids of the member's own sale-only listings (plan §5). A wants-my-pedals row
 * for one of these is a purchase lead, not a trade opportunity, so the
 * Trade Opportunities count, the Potential Match banners and the
 * recommendations all drop those rows. Empty set on a read failure so the
 * caller behaves as it did before the field existed.
 */
const saleOnlyCache = new Map(); // uid -> { at, promise }
const SALE_ONLY_CACHE_MS = 30 * 1000;

export async function getMySaleOnlyPedalIds(userId) {
  if (!userId) return new Set();
  // One read per member per half minute, shared by the dashboard, browse,
  // Saved Pedals and the badge count (review 2026-10-20: these each read it).
  const hit = saleOnlyCache.get(userId);
  if (hit && Date.now() - hit.at < SALE_ONLY_CACHE_MS) return hit.promise;
  const promise = (async () => {
    const ids = new Set();
    try {
      const snap = await getDocs(query(collection(db, 'listings'), where('ownerId', '==', userId), where('active', '==', true)));
      snap.docs.forEach((d) => { if (offerModeOf(d.data()) === 'sell') ids.add(d.id); });
    } catch (err) {
      console.error('Could not read own listings for sale-only filter:', err);
      saleOnlyCache.delete(userId);
    }
    return ids;
  })();
  saleOnlyCache.set(userId, { at: Date.now(), promise });
  return promise;
}

/**
 * Count the user's "new" Wantlist Matches — pedals that appeared on the
 * platform matching their Wantlist since they last viewed the page.
 *
 * A pedal is "new" when its listing TRIGGERED the match and went live after the
 * cursor — derived live from stable timestamps (no wmp.createdAt, no
 * triggerSide):
 *   - listing-triggered: listingListedAt(pedal) > earliest matching want's
 *     createdAt. The listing came (or came back) live after a want the user
 *     already had, so its appearance is the genuine discovery. The inverse
 *     (the user just added/broadened a want that matched pre-existing listings)
 *     is NOT counted — those listings predate the want.
 *   - fresh: listingListedAt(pedal) > wlmMs (the last-viewed cursor).
 *
 * Using listingListedAt (reactivatedAt-aware) rather than raw createdAt means a
 * reactivated listing that now matches a standing want counts as a discovery.
 *
 * Returns the raw count (0 when nothing new). Callers cap display at "9+".
 */
export async function getNewWantListMatchCount(userId, { wmpRows = null } = {}) {
  if (!userId) return 0;
  try {
    const { wlmMs } = await readViewedCursors(userId);

    // `wmpRows`: the wantingUserId == me rows the live listener already holds
    // (subscribeToMatchCounters). Absent, run the query as before.
    const rows = wmpRows || (await getDocs(
      query(
        collection(db, 'wantsMyPedals'),
        where('wantingUserId', '==', userId)
      )
    )).docs.map((d) => d.data());
    if (rows.length === 0) return 0;

    // Collect (pedalId, wantId) pairs. wmp.createdAt is read ONLY as a bounding
    // hint to pick the most-likely-recent candidates before the QUERY_LIMIT cap
    // on downstream reads — never for the new/old decision below.
    const candidates = [];
    rows.forEach((data) => {
      candidates.push({
        pedalId: data.pedalId,
        wantId: data.wantId,
        hintMs: tsToMs(data.createdAt)
      });
    });

    candidates.sort((a, b) => b.hintMs - a.hintMs);
    const trimmed = candidates.slice(0, QUERY_LIMIT);

    const uniquePedalIds = [...new Set(trimmed.map((c) => c.pedalId))];
    const uniqueWantIds = [...new Set(trimmed.map((c) => c.wantId).filter(Boolean))];
    const [listingMap, wantMap] = await Promise.all([
      fetchListingsByIds(uniquePedalIds),
      uniqueWantIds.length ? fetchWantsByIds(uniqueWantIds) : Promise.resolve(new Map())
    ]);

    // A pedal can match several of my wants; the listing is a discovery if it
    // went live after the EARLIEST such want existed. Collect that min per pedal.
    const earliestWantMsByPedal = new Map();
    for (const { pedalId, wantId } of trimmed) {
      const want = wantMap.get(wantId);
      if (!want) continue;
      const wantMs = tsToMs(want.createdAt);
      const prev = earliestWantMsByPedal.get(pedalId);
      if (prev == null || wantMs < prev) earliestWantMsByPedal.set(pedalId, wantMs);
    }

    let count = 0;
    const seen = new Set();
    for (const { pedalId } of trimmed) {
      if (seen.has(pedalId)) continue;
      seen.add(pedalId);
      const listing = listingMap.get(pedalId);
      if (isExcludedForWantListMatches(listing, userId)) continue;

      const listedMs = listingListedAtMs(listing);
      if (listedMs <= wlmMs) continue; // not fresh since last visit

      const earliestWantMs = earliestWantMsByPedal.get(pedalId);
      // Listing-triggered: the listing went live after a want I already had.
      // If we couldn't resolve the want, fall back to freshness alone.
      if (earliestWantMs != null && listedMs <= earliestWantMs) continue;

      count += 1;
      if (count > DISPLAY_CAP) break; // We have enough to render "9+"
    }
    return count;
  } catch (err) {
    console.error('❌ Error counting new Wantlist matches:', err);
    return 0;
  }
}

/**
 * Count the user's "new" Match Opportunities.
 *
 * The Match Opportunities carousel (src/utils/recommendations.js) shows a card
 * for EVERY active pedal owned by anyone who wants one of the user's pedals —
 * i.e. the interested users' tradeable inventory, not the wants themselves. So
 * the badge counts the same thing the eye sees: carousel cards that became
 * visible since the last-viewed cursor. (Counting wmp "wanting relationships"
 * instead made the badge disagree with the carousel — one new wanter with four
 * listed pedals showed four cards but a badge of one.)
 *
 * A card (interested user U's pedal P) first becomes visible at
 *   max(relationshipStart(U), listedAt(P))
 * where relationshipStart(U) is the earliest createdAt across U's wants that
 * match any of the current user's pedals (when U's inventory first entered the
 * view). It counts as "new" when that moment is after the cursor. This handles
 * both "a new person started wanting my pedal" (relationshipStart is recent)
 * and "an existing interested person listed a new pedal" (listedAt is recent).
 *
 * relationshipStart is derived from the stable `wantList.createdAt`, never from
 * `wmp.createdAt` (which drifts on re-writes). `wmp.createdAt` is used only as
 * a bounding hint to pick which users to scan under the fan-out cap.
 *
 * Returns the raw count (0 when nothing new). Callers cap display at "9+".
 *
 * `options.wmpRows`: pre-fetched wantsMyPedals row data for this owner (the
 * dashboard fetches the rows once and shares them across its consumers — see
 * fetchWantsMyPedalsRows in recommendations.js). Absent/null, this runs its
 * own query exactly as before; the realtime listener recompute path always
 * calls without rows.
 */
export async function getNewMatchOpportunitiesCount(userId, { wmpRows = null } = {}) {
  if (!userId) return 0;
  try {
    const { moMs } = await readViewedCursors(userId);

    const wmpRowDataAll = wmpRows || (await getDocs(
      query(
        collection(db, 'wantsMyPedals'),
        where('pedalOwnerId', '==', userId)
      )
    )).docs.map((d) => d.data());
    if (wmpRowDataAll.length === 0) return 0;
    // Rows about my own sale-only pedals are purchase leads, not trade opportunities.
    const mySaleOnly = await getMySaleOnlyPedalIds(userId);
    const wmpRowData = mySaleOnly.size ? wmpRowDataAll.filter((r) => !mySaleOnly.has(r.pedalId)) : wmpRowDataAll;
    if (wmpRowData.length === 0) return 0;

    // Group by interested user: the wantIds connecting them to my pedals, plus
    // a recency hint (max wmp.createdAt) used ONLY to pick which users to scan.
    const wantIdsByUser = new Map(); // wantingUserId -> Set<wantId>
    const hintMsByUser = new Map(); // wantingUserId -> max wmp.createdAt
    wmpRowData.forEach((data) => {
      const uid = data.wantingUserId;
      if (!uid) return;
      if (!wantIdsByUser.has(uid)) wantIdsByUser.set(uid, new Set());
      if (data.wantId) wantIdsByUser.get(uid).add(data.wantId);
      const hintMs = tsToMs(data.createdAt);
      const prevHint = hintMsByUser.get(uid);
      if (prevHint == null || hintMs > prevHint) hintMsByUser.set(uid, hintMs);
    });
    if (wantIdsByUser.size === 0) return 0;

    // Bound the fan-out: cap the number of interested users we scan (their
    // wants and listings are then fetched in batches of 30). Pick the most-recently-interested first
    // (by wmp.createdAt hint), since they're the likeliest to carry new cards.
    // We only need DISPLAY_CAP+1 new cards to render "9+".
    // (Edge case: a long-idle wanter beyond the cap who just listed a new pedal
    // could be missed — acceptable for the badge.)
    const interestedUserIds = [...wantIdsByUser.keys()]
      .sort((a, b) => (hintMsByUser.get(b) || 0) - (hintMsByUser.get(a) || 0))
      .slice(0, MAX_INTERESTED_USERS_SCANNED);

    // relationshipStart(U) = min(want.createdAt) over U's matching wants —
    // computed from the stable wantList docs.
    const allWantIds = [
      ...new Set(interestedUserIds.flatMap((uid) => [...wantIdsByUser.get(uid)]))
    ];
    const wantMap = allWantIds.length ? await fetchWantsByIds(allWantIds) : new Map();
    const relationshipStartByUser = new Map();
    for (const uid of interestedUserIds) {
      let minMs = Infinity;
      for (const wantId of wantIdsByUser.get(uid)) {
        const want = wantMap.get(wantId);
        if (!want) continue;
        const ms = tsToMs(want.createdAt);
        if (ms < minMs) minMs = ms;
      }
      relationshipStartByUser.set(uid, minMs === Infinity ? 0 : minMs);
    }

    const listingsByOwner = await fetchActiveListingsByOwners(interestedUserIds);

    let count = 0;
    const countedPedalIds = new Set(); // dedupe cards by offered pedal id
    for (const uid of interestedUserIds) {
      const relStartMs = relationshipStartByUser.get(uid) || 0;
      for (const d of listingsByOwner.get(uid) || []) {
        if (countedPedalIds.has(d.id)) continue;
        const pedal = d.data;
        if (isHiddenFromMatchOpportunities(pedal)) continue;
        // The card became visible at the later of: U started wanting my pedal,
        // or U listed this pedal. New iff that moment is past the cursor.
        const visibleSinceMs = Math.max(relStartMs, listingListedAtMs(pedal));
        if (visibleSinceMs > moMs) {
          countedPedalIds.add(d.id);
          count += 1;
          if (count > DISPLAY_CAP) return count; // enough to render "9+"
        }
      }
    }
    return count;
  } catch (err) {
    console.error('❌ Error counting new match opportunities:', err);
    return 0;
  }
}

/**
 * Reset the Wantlist Matches counter — call when the user lands on
 * /want-list-matches. Idempotent.
 */
export async function markWantListMatchesViewed(userId) {
  if (!userId) return;
  try {
    await updateDoc(doc(db, 'users', userId), {
      lastViewedWantListMatchesAt: serverTimestamp()
    });
    console.log('✅ Marked Wantlist matches viewed');
  } catch (err) {
    console.error('❌ Error marking Wantlist matches viewed:', err);
  }
}

/**
 * Reset the Match Opportunities counter — call when the user lands on
 * /recommendations. Idempotent.
 */
export async function markMatchOpportunitiesViewed(userId) {
  if (!userId) return;
  try {
    await updateDoc(doc(db, 'users', userId), {
      lastViewedMatchOpportunitiesAt: serverTimestamp()
    });
    console.log('✅ Marked match opportunities viewed');
  } catch (err) {
    console.error('❌ Error marking match opportunities viewed:', err);
  }
}

/**
 * Display helper — caps at DISPLAY_CAP for "9+" rendering. Caller decides
 * whether to render at all (typically only when count > 0).
 */
export function formatBadgeCount(n) {
  if (!n || n <= 0) return '0';
  if (n > DISPLAY_CAP) return `${DISPLAY_CAP}+`;
  return String(n);
}

/**
 * Subscribe to live updates for both dashboard badge counters. Calls
 * `onCountsChange({ wlmCount, moCount })` after the first computation and
 * every time the underlying `wantsMyPedals` collection changes for this user
 * (in either direction: a new doc arriving = +1, an existing doc
 * disappearing = potentially -1).
 *
 * THE SUBSCRIPTION OWNS THE COMPUTATION (Stage 2, 2026-10-31). Before, the
 * dashboard computed both counts on mount and then, 1.5 s later, started
 * these listeners, whose initial snapshots report every document as a
 * change and so triggered a second full pass over the same data: two
 * complete computations per dashboard load, observed in production. Now the
 * dashboard only claims the first-visit cursors and then subscribes; the
 * first computation runs once BOTH listeners have delivered their initial
 * snapshot, from the rows those snapshots carry (no separate wantsMyPedals
 * queries). Nothing can slip between "first computation" and "listener
 * start" because the listener is the only source: a write that lands after
 * the initial snapshot arrives as a delta and schedules a recompute.
 *
 * Why two listeners + a debounced recompute:
 *   - Two listeners are required because the rules require either
 *     `pedalOwnerId == me` or `wantingUserId == me` to grant read access.
 *     Firestore doesn't support OR queries, so we must subscribe twice.
 *   - On every change we re-run the full counter logic
 *     (`getNewWantListMatchCount` / `getNewMatchOpportunitiesCount`) instead
 *     of trying to bump counts incrementally. The full path applies the
 *     availability/ghost filter (inactive/inTrade/hidden listings) and the
 *     listed-at vs cursor / relationship-start timestamp tests — any of which
 *     can change a count without a wmp doc itself moving.
 *   - The recompute is debounced so a burst of matchmaker writes
 *     (e.g. 5 wmp docs in 100ms when one listing matches 5 wants) collapses
 *     into a single read pass instead of 5 sequential ones.
 *
 * Listing deactivation: covered transparently because
 * `cleanupInvalidMatches` (functions/cleanupMatches.js) fires on the same
 * `listings/{pedalId}` onUpdate event as the matchmaker, detects
 * `becameInactive || enteredTrade`, and calls `cleanupWantsMyPedalsForPedal`
 * to delete every wmp doc for that pedal. Those deletions DO produce wmp
 * snapshot events, so this listener wakes up and recomputes.
 *
 * Returns an unsubscribe function. Caller MUST invoke it on cleanup
 * (logout, dashboard unmount) to avoid stacking listeners across users; after
 * it is called no result, however late, reaches `onCountsChange`. The
 * function also carries `.recompute()` for explicit refreshes
 * (pull-to-refresh): it re-reads cursors/listings/wants against the rows the
 * listeners currently hold.
 *
 * `options.firstPass`: `{ wlmInitialized, moInitialized }` from
 * markFirstVisitIfNeeded. A counter whose cursor was claimed just now is
 * necessarily 0 on the first pass, so it is reported as 0 without reads;
 * later recomputes always count for real.
 *
 * Failures inside the recompute are swallowed (the count helpers already
 * log + return 0). The listener itself reports onSnapshot errors via
 * console.error — typically permission-denied during auth transitions,
 * which is benign and self-resolves.
 */
export function subscribeToMatchCounters(userId, onCountsChange, { firstPass = {} } = {}) {
  if (!userId || typeof onCountsChange !== 'function') {
    const noop = () => {};
    noop.recompute = () => Promise.resolve();
    return noop;
  }

  let cancelled = false;
  let debounceTimer = null;
  let debounceFirstScheduledAt = null;
  // Rows held by each listener (null until its initial snapshot arrives).
  const rows = { wlm: null, mo: null };
  let firstPassPending = true;

  const recompute = async () => {
    if (cancelled) return;
    debounceFirstScheduledAt = null;
    try {
      // Rows as of run start; a delta mid-run re-runs on newer rows.
      const first = firstPassPending;
      firstPassPending = false;
      const [wlmCount, moCount] = await Promise.all([
        first && firstPass.wlmInitialized ? 0 : getNewWantListMatchCount(userId, { wmpRows: rows.wlm || [] }),
        first && firstPass.moInitialized ? 0 : getNewMatchOpportunitiesCount(userId, { wmpRows: rows.mo || [] })
      ]);
      if (cancelled) return;
      onCountsChange({ wlmCount, moCount });
    } catch (err) {
      console.error('❌ Error recomputing match counters:', err);
    }
  };

  // Execution serialization (Phase 2d-1, 2026-09-29): the debounce below
  // shapes SCHEDULING, but recompute is async — a run slower than the 500ms
  // trailing window used to overlap the next one, with no aggregate bound.
  // Every trigger now goes through a run-and-queue-latest runner: at most
  // one recompute in flight, at most one queued, triggers during a run
  // coalesce into the single trailing re-run (see coalescingRunner.js).
  const runRecompute = createCoalescingRunner(recompute);

  // Trailing debounce WITH a max-wait. The 2026-09-25 iOS listen storm
  // (identical snapshot re-deliveries every ~8ms) would reset a plain
  // trailing timer forever, starving the recompute for the storm's whole
  // duration. The max-wait guarantees the counters compute within 3s of the
  // first delivery no matter how fast deliveries keep arriving; the empty-
  // delta skip at the call sites keeps identical re-deliveries from even
  // scheduling.
  const scheduleRecompute = () => {
    const now = Date.now();
    if (debounceFirstScheduledAt === null) debounceFirstScheduledAt = now;
    if (now - debounceFirstScheduledAt >= 3000) {
      if (debounceTimer) clearTimeout(debounceTimer);
      debounceTimer = null;
      runRecompute();
      return;
    }
    if (debounceTimer) clearTimeout(debounceTimer);
    debounceTimer = setTimeout(runRecompute, 500);
  };

  const onDelta = (side) => (snapshot) => {
    const initial = rows[side] === null;
    rows[side] = snapshot.docs.map((d) => d.data());
    // Identical re-delivery (no doc changes, no local pending writes) carries
    // no new information — don't even reset the debounce for it.
    if (!initial && snapshot.docChanges().length === 0 && !snapshot.metadata.hasPendingWrites) return;
    // The first computation waits for BOTH initial snapshots so it sees one
    // consistent state and runs exactly once; either side's later deltas
    // schedule as usual.
    if (rows.wlm === null || rows.mo === null) return;
    scheduleRecompute();
  };

  // Listener A: wmp docs where the user is the wanter (Wantlist Matches)
  const wlmUnsub = onSnapshot(
    query(
      collection(db, 'wantsMyPedals'),
      where('wantingUserId', '==', userId)
    ),
    onDelta('wlm'),
    (err) => {
      // Permission-denied during auth transition is the most common case;
      // the next valid auth state will rebind a fresh listener via the
      // hook's effect cleanup. Higher log level for anything else.
      if (err?.code !== 'permission-denied') {
        console.error('❌ Wantlist Matches listener error:', err);
      }
    }
  );

  // Listener B: wmp docs where the user is the pedal owner (Match Opportunities)
  const moUnsub = onSnapshot(
    query(
      collection(db, 'wantsMyPedals'),
      where('pedalOwnerId', '==', userId)
    ),
    onDelta('mo'),
    (err) => {
      if (err?.code !== 'permission-denied') {
        console.error('❌ Match Opportunities listener error:', err);
      }
    }
  );

  const unsubscribe = () => {
    cancelled = true;
    runRecompute.cancel();
    if (debounceTimer) clearTimeout(debounceTimer);
    wlmUnsub();
    moUnsub();
  };
  unsubscribe.recompute = () =>
    (rows.wlm === null || rows.mo === null ? Promise.resolve() : Promise.resolve(runRecompute()));
  return unsubscribe;
}
